/* Scroll story: frames scrubbed by scroll, chapter copy that passes behind the glass on wide screens.
   <scroll-story> sets itself up on connect and tears everything down on disconnect, so the theme editor can
   re-render the section freely. Static chapter flow (no JS, reduced motion, editor) needs nothing from here. */
(function () {
  'use strict';
  if (customElements.get('scroll-story')) return;

  var MATTE_GRID = { land: { cols: 18, rows: 15 }, port: { cols: 27, rows: 10 } };
  /* two renders of the same camera move: landscape for wide screens, portrait for phones.
     portrait ships every other frame (odd frame numbers); the nearest loaded frame fills the gaps */
  var SETS = { land: { w: 1920, h: 1080, step: 1 }, port: { w: 900, h: 1600, step: 2 } };

  function pad(n) { return String(n).padStart(4, '0'); }
  /* he-land-0001.webp?v=123 -> he-land-0042.webp (asset URLs resolve without the version param) */
  function frameUrl(tpl, n) { return tpl.replace(/\d{4}\.webp(\?.*)?$/, pad(n) + '.webp'); }
  function isCrossOrigin(u) { try { return new URL(u, location.href).origin !== location.origin; } catch (e) { return false; } }
  function hexRgb(h) {
    h = h.replace('#', '').trim();
    if (h.length === 3) h = h.replace(/./g, '$&$&');
    return [0, 2, 4].map(function (i) { return parseInt(h.substr(i, 2), 16) / 255; });
  }

  class ScrollStory extends HTMLElement {
    connectedCallback() {
      this.cleanups = [];
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.hasAttribute('data-static') || reduce || !window.gsap || !window.ScrollTrigger) { this.classList.add('is-static'); return; }
      try { this.init(); } catch (e) { console.error('scroll-story', e); this.destroy(); }
    }
    disconnectedCallback() { this.destroy(); }
    on(target, type, fn, opts) { target.addEventListener(type, fn, opts); this.cleanups.push(function () { target.removeEventListener(type, fn, opts); }); }
    destroy() {
      (this.cleanups || []).splice(0).reverse().forEach(function (fn) { try { fn(); } catch (e) {} });
      this.classList.remove('is-scrub', 'glass-off');
    }

    init() {
      var self = this, root = this;
      gsap.registerPlugin(ScrollTrigger);
      var FRAMES = parseInt(root.dataset.frames, 10) || 270;
      var tpl = { land: root.dataset.landSrc, port: root.dataset.portSrc };
      var MATTE = { land: root.dataset.matteLand, port: root.dataset.mattePort };
      var NORMALS = { land: root.dataset.normalLand, port: root.dataset.normalPort };
      var cross = isCrossOrigin(tpl.land);
      var tainted = false;   /* a cross-origin host without CORS: frames draw, glass is skipped */

      // no fade while switching from the load state to scrub: the chapters were opaque a moment ago and
      // would otherwise all fade out together (a flash of every chapter). Fades come back after two frames.
      root.classList.add('ss-instant');
      root.classList.add('is-scrub');
      requestAnimationFrame(function () { requestAnimationFrame(function () { root.classList.remove('ss-instant'); }); });
      root.style.setProperty('--story-h', Math.round(FRAMES * 4.2) + 'vh');
      this.cleanups.push(function () { root.style.removeProperty('--story-h'); });

      var callouts = Array.prototype.slice.call(root.querySelectorAll('.callout')).map(function (el) {
        var r = (el.dataset.on || '0-0').split('-'); return { el: el, a: +r[0], b: +r[1] };
      });

      /* ---- smooth scrolling, paused while any drawer or dialog is open ---- */
      var lenis = null;
      if (window.Lenis) {
        lenis = new Lenis({
          lerp: 0.1, smoothWheel: true,
          prevent: function (node) { return !!(node.closest && node.closest('.drawer, dialog, [data-lenis-prevent]')); }
        });
        lenis.on('scroll', ScrollTrigger.update);
        var tick = function (t) { lenis.raf(t * 1000); };
        gsap.ticker.add(tick);
        gsap.ticker.lagSmoothing(0);
        if (document.body.classList.contains('has-overlay')) lenis.stop();
        this.on(document, 'overlay:open', function () { lenis.stop(); });
        this.on(document, 'overlay:close', function (e) { if (!e.detail || !e.detail.open) lenis.start(); });
        this.cleanups.push(function () { gsap.ticker.remove(tick); gsap.ticker.lagSmoothing(500, 33); lenis.destroy(); });
      }

      var canvas = root.querySelector('[data-stage]'), ctx = null, gl = null;
      try { gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false }); } catch (e) { gl = null; }
      if (!gl) ctx = canvas.getContext('2d');
      var box = root.querySelector('[data-frame-box]');
      var setName = null, imgs = [], loaded = 0, current = 0, gen = 0;
      var rect = { x: 0, y: 0, w: 0, h: 0 }, shift = { fx: 0 };
      var loadingEl = root.querySelector('[data-loading-el]');
      var loadingCount = root.dataset.loadingCount || 'Loading frames [loaded] of [total]';
      function pickSet() { return canvas.clientWidth >= canvas.clientHeight * 0.9 ? 'land' : 'port'; }
      function src(i) { return frameUrl(tpl[setName], i + 1); }
      function newImage(url, onload, onerror) {
        var im = new Image(); im.decoding = 'async';
        if (cross && !tainted) im.crossOrigin = 'anonymous';
        im.onload = onload;
        im.onerror = function () {
          /* CORS refused: retry plain, and give up on the glass for this page view */
          if (im.crossOrigin && !im._retried) { im._retried = true; tainted = true; im.removeAttribute('crossorigin'); im.crossOrigin = null; im.src = url + (url.indexOf('?') < 0 ? '?' : '&') + 'nc=1'; }
          else if (onerror) onerror();
        };
        im.src = url;
        return im;
      }
      function loadSet(name) {
        setName = name; imgs = new Array(FRAMES); loaded = 0; loadingEl.hidden = false;
        var token = ++gen, failed = 0;
        /* current frame first, then outward so scrubbing near the start is never blank */
        var step = SETS[name].step, start = current - (current % step);
        var order = [start];
        for (var d = step; d < FRAMES; d += step) { if (start + d < FRAMES) order.push(start + d); if (start - d >= 0) order.push(start - d); }
        var total = order.length;
        /* a frame that fails is skipped (the nearest loaded frame fills in); if none load at all,
           fall back to the static chapter flow instead of an empty stage */
        function settle() {
          if (loaded + failed < total) { loadingEl.textContent = loadingCount.replace('[loaded]', loaded).replace('[total]', total); return; }
          loadingEl.hidden = true;
          if (!loaded) { self.destroy(); self.classList.add('is-static'); }
        }
        order.forEach(function (i) {
          var im = newImage(src(i), function () {
            if (gen !== token) return;
            imgs[i] = im; loaded++;
            if (i === current || loaded === 1) { updateMask(); draw(current); }
            settle();
          }, function () {
            if (gen !== token) return;
            failed++;
            settle();
          });
        });
        callouts.forEach(function (c) {
          var p = (c.el.dataset[name] || '50,50').split(',');
          c.el.style.left = p[0] + '%'; c.el.style.top = p[1] + '%';
        });
      }
      this.cleanups.push(function () { gen++; imgs = []; });

      function size() {
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var w = canvas.clientWidth, h = canvas.clientHeight;
        if (!w || !h) return;
        var W = Math.round(w * dpr), H = Math.round(h * dpr);
        if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0); }
        var want = pickSet(); if (want !== setName) loadSet(want);
        var F = SETS[setName];
        /* cover the stage; the backdrop is the render's own grey, so sliding the frame sideways never shows an edge */
        var sc = Math.max(w / F.w, h / F.h);
        var fw = F.w * sc, fh = F.h * sc;
        var dx = (w > 820 ? shift.fx * w : 0);
        rect = { x: (w - fw) / 2 + dx, y: (h - fh) / 2, w: fw, h: fh };
        box.style.left = rect.x + 'px'; box.style.top = rect.y + 'px';
        if (intro) {   /* where the rendered badge sits in the landscape frame, so the caption hangs under it */
          root.style.setProperty('--intro-x', (rect.x + rect.w * 0.281) + 'px');
          root.style.setProperty('--intro-y', (rect.y + rect.h * 0.352) + 'px');
          root.style.setProperty('--intro-d', Math.round(rect.h * 0.425) + 'px');
        }
        box.style.width = rect.w + 'px'; box.style.height = rect.h + 'px';
        updateMask(); draw(current);
      }
      function nearestIndex(i) {
        if (imgs[i]) return i;
        for (var d = 1; d < FRAMES; d++) {
          if (imgs[i - d]) return i - d;
          if (imgs[i + d]) return i + d;
        }
        return -1;
      }
      function nearest(i) { var k = nearestIndex(i); return k < 0 ? null : imgs[k]; }
      var stageColor = getComputedStyle(root).getPropertyValue('--stage').trim() || '#D9D9D9';

      /* WebGL draws the frame; when the glass effect is on, clear quartz turns see-through (except its edges and
         highlights) so the text layers underneath show through it, while frosted and opaque parts stay solid */
      var G = null, glassMode = false, matteDirty = true;
      function setupGL() {
        var vs = 'attribute vec2 aPos; attribute vec2 aUv; varying vec2 vUv; void main(){ vUv = aUv; gl_Position = vec4(aPos, 0.0, 1.0); }';
        var fs = 'precision mediump float; uniform sampler2D uFrame; uniform sampler2D uMatte; uniform vec3 uBg; uniform float uGlass; varying vec2 vUv;' +
          'void main(){ vec3 c = texture2D(uFrame, vUv).rgb;' +
          ' if (uGlass < 0.5) { gl_FragColor = vec4(c, 1.0); return; }' +
          ' float m = texture2D(uMatte, vUv).a;' +
          ' float detail = smoothstep(0.07, 0.42, distance(c, uBg));' +
          ' float inside = smoothstep(0.04, 0.3, m);' +
          ' float solid = smoothstep(0.62, 0.95, m);' +
          ' float a = max(inside * clamp(detail * 0.8, 0.05, 0.72), solid);' +
          ' gl_FragColor = vec4(c * a, a); }';
        function sh(type, s) { var o = gl.createShader(type); gl.shaderSource(o, s); gl.compileShader(o); return o; }
        var prog = gl.createProgram(); gl.attachShader(prog, sh(gl.VERTEX_SHADER, vs)); gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, fs)); gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
        gl.useProgram(prog);
        var buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        var aPos = gl.getAttribLocation(prog, 'aPos'), aUv = gl.getAttribLocation(prog, 'aUv');
        gl.enableVertexAttribArray(aPos); gl.enableVertexAttribArray(aUv);
        gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0); gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 16, 8);
        function tex(unit) {
          var t = gl.createTexture(); gl.activeTexture(gl.TEXTURE0 + unit); gl.bindTexture(gl.TEXTURE_2D, t);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE); return t;
        }
        var g = { frameTex: tex(0), matteTex: tex(1), lastImg: null, uGlass: gl.getUniformLocation(prog, 'uGlass') };
        gl.uniform1i(gl.getUniformLocation(prog, 'uFrame'), 0); gl.uniform1i(gl.getUniformLocation(prog, 'uMatte'), 1);
        gl.uniform3fv(gl.getUniformLocation(prog, 'uBg'), hexRgb(stageColor));
        return g;
      }
      if (gl) { G = setupGL(); if (!G) { gl = null; } }
      /* WebGL refused (no context, link failure or a tainted image): swap in a plain 2D canvas */
      function fallback2D() {
        if (gl) { var ext = gl.getExtension('WEBGL_lose_context'); if (ext) ext.loseContext(); }
        gl = null; G = null;
        var c2 = document.createElement('canvas'); c2.setAttribute('aria-hidden', 'true'); c2.setAttribute('data-stage', '');
        canvas.parentNode.replaceChild(c2, canvas); canvas = c2; ctx = canvas.getContext('2d');
        canvas.width = 0; disableGlass(); size();
      }
      this.cleanups.push(function () { if (gl) { var ext = gl.getExtension('WEBGL_lose_context'); if (ext) ext.loseContext(); } gl = null; G = null; });

      function draw(i) {
        var im = nearest(i);
        if (!G) {
          if (!ctx) return;
          ctx.fillStyle = stageColor; ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);
          if (im) { ctx.drawImage(im, rect.x, rect.y, rect.w, rect.h); if (!introPlayed && intro) { introPlayed = true; playIntro(); } }
          return;
        }
        var w = canvas.clientWidth, h = canvas.clientHeight;
        gl.viewport(0, 0, canvas.width, canvas.height); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
        if (!im) return;
        if (!introPlayed && intro) { introPlayed = true; requestAnimationFrame(playIntro); }
        try {
          if (G.lastImg !== im) { gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, G.frameTex); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, im); G.lastImg = im; }
          var useGlass = glassMode && mcv.width > 1;
          if (useGlass && matteDirty) { gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, G.matteTex); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, mcv); matteDirty = false; }
        } catch (e) { fallback2D(); return; }
        gl.uniform1f(G.uGlass, useGlass ? 1 : 0);
        var x0 = rect.x / w * 2 - 1, x1 = (rect.x + rect.w) / w * 2 - 1, y0 = 1 - (rect.y + rect.h) / h * 2, y1 = 1 - rect.y / h * 2;
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([x0, y0, 0, 1, x1, y0, 1, 1, x0, y1, 0, 0, x1, y1, 1, 0]), gl.STREAM_DRAW);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }

      var chapters = Array.prototype.slice.call(root.querySelectorAll('.chapter'));
      var intro = root.querySelector('.chapter.intro'), introTo = intro ? (+intro.dataset.to || 13) : -1, introPlayed = false;
      /* the badge is in the render itself; the intro only fades its caption and cue in once the first frame is up */
      function playIntro() { root.classList.add('intro-play'); }

      this.cleanups.push(function () { root.classList.remove('intro-play', 'at-intro'); ['--intro-p', '--intro-x', '--intro-y', '--intro-d'].forEach(function (k) { root.style.removeProperty(k); }); });

      /* ---- text behind glass ---- */
      var glassOK = !!(window.CSS && CSS.supports && (CSS.supports('mask-composite', 'exclude') || CSS.supports('-webkit-mask-composite', 'xor')));
      var chaptersEl = root.querySelector('[data-chapters]'), ghost = null, ghostChapters = [];
      var stageEl = canvas.parentNode, glassLayer = null, uiLayer = null;
      var filterId = 'glassWarp-' + (root.id || '').replace('ScrollStory-', '');
      if (glassOK && G) {
        ghost = chaptersEl.cloneNode(true);
        ghost.removeAttribute('data-chapters'); ghost.classList.add('chapters-ghost');
        ghost.setAttribute('aria-hidden', 'true'); ghost.setAttribute('inert', '');
        ghost.querySelectorAll('[id]').forEach(function (e) { e.removeAttribute('id'); });
        ghost.querySelectorAll('[data-shopify-editor-block]').forEach(function (e) { e.removeAttribute('data-shopify-editor-block'); });
        ghost.querySelectorAll('a, button').forEach(function (e) { e.setAttribute('tabindex', '-1'); });
        chaptersEl.parentNode.insertBefore(ghost, chaptersEl.nextSibling);
        ghostChapters = Array.prototype.slice.call(ghost.querySelectorAll('.chapter'));
        chaptersEl.classList.add('behind-glass-sharp');
        /* move the canvas above the text, and the labels/rail above the canvas */
        glassMode = true;
        glassLayer = document.createElement('div'); glassLayer.className = 'glass-layer'; glassLayer.setAttribute('aria-hidden', 'true');
        uiLayer = document.createElement('div'); uiLayer.className = 'ui-layer';
        glassLayer.appendChild(canvas);
        ['[data-frame-box]', '[data-loading-el]', '[data-rail]'].forEach(function (q) { var e = stageEl.querySelector(q); if (e) uiLayer.appendChild(e); });
        ghost.parentNode.insertBefore(glassLayer, ghost.nextSibling);
        glassLayer.parentNode.insertBefore(uiLayer, glassLayer.nextSibling);
      }
      this.cleanups.push(function () {
        /* put the DOM back the way Liquid rendered it */
        if (uiLayer) { Array.prototype.slice.call(uiLayer.children).forEach(function (e) { stageEl.appendChild(e); }); uiLayer.remove(); }
        /* a fresh canvas: the old one's WebGL context is about to be lost and can't be reused */
        var fresh = document.createElement('canvas'); fresh.setAttribute('data-stage', ''); fresh.setAttribute('aria-hidden', 'true');
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
        stageEl.insertBefore(fresh, stageEl.firstChild);
        if (glassLayer) glassLayer.remove();
        root.querySelectorAll('.chapters-ghost').forEach(function (e) { e.remove(); });
        chaptersEl.classList.remove('behind-glass-sharp');
        maskStyle(chaptersEl, '', '', '');
        chapters.forEach(function (ch) { ch.classList.remove('on'); ch.removeAttribute('aria-hidden'); });
        callouts.forEach(function (c) { c.el.classList.remove('on'); c.el.style.left = c.el.style.top = ''; });
        box.removeAttribute('style');
      });
      if (!gl && !ctx) fallback2D();
      function maskStyle(el, image, sz, pos) {
        el.style.webkitMaskImage = image; el.style.maskImage = image;
        el.style.webkitMaskSize = sz; el.style.maskSize = sz;
        el.style.webkitMaskPosition = pos; el.style.maskPosition = pos;
      }
      function disableGlass() {
        if (ghost) { maskStyle(ghost, 'none', 'auto', '0 0'); ghost.style.filter = ''; }
        maskStyle(chaptersEl, 'none', 'auto', '0 0');
        glassMode = false; ghost = null; root.classList.add('glass-off');
      }
      /* the current frame's matte is cut from the sprite onto a small canvas and used as the mask image,
         so the mask is never larger than one frame (huge scaled sprites trip up mobile browsers) */
      var sheets = {}, mcv = document.createElement('canvas'), mctx = mcv.getContext('2d'), lastMaskKey = '';
      var kcv = document.createElement('canvas'), kctx = kcv.getContext('2d');   /* boosted copy: any glass at all hides the sharp text */
      var nsheets = {}, ncv = document.createElement('canvas'), nctx = ncv.getContext('2d'), lastNormKey = '';
      var feImg = root.querySelector('[data-glass-normal]'), feDisp = root.querySelector('[data-glass-disp]');
      function nsheet(name) {
        if (!nsheets[name]) nsheets[name] = newImage(NORMALS[name], function () { lastNormKey = ''; updateMask(); });
        return nsheets[name];
      }
      /* refraction: the blurred copy is displaced by the glass surface direction, so text bends across the tube like a lens */
      function updateRefraction() {
        if (!ghost || !glassMode || setName !== 'land') return;   /* portrait never shows the glass: skip the normal map */
        var im = nsheet(setName), M = MATTE_GRID[setName];
        if (!im.complete || !im.naturalWidth) return;
        var cw = im.naturalWidth / M.cols, chh = im.naturalHeight / M.rows, shown = nearestIndex(current); if (shown < 0) shown = current;
        var key = setName + shown;
        if (key !== lastNormKey) {
          if (ncv.width !== cw || ncv.height !== chh) { ncv.width = cw; ncv.height = chh; }
          nctx.drawImage(im, (shown % M.cols) * cw, Math.floor(shown / M.cols) * chh, cw, chh, 0, 0, cw, chh);
          var url; try { url = ncv.toDataURL('image/png'); } catch (e) { return; }   // tainted: keep the plain blur
          feImg.setAttribute('href', url); feImg.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', url);
          lastNormKey = key;
          ghost.style.filter = 'url(#' + filterId + ')';
        }
        feImg.setAttribute('x', rect.x); feImg.setAttribute('y', rect.y);
        feImg.setAttribute('width', rect.w); feImg.setAttribute('height', rect.h);
        feDisp.setAttribute('scale', Math.round(rect.w * 0.085));
      }
      function sheet(name) {
        if (!sheets[name]) sheets[name] = newImage(MATTE[name], function () { lastMaskKey = ''; updateMask(); draw(current); });
        return sheets[name];
      }
      function updateMask() {
        if (!setName) return;
        var on = !!(ghost && G && setName === 'land');
        if (on !== glassMode || root.classList.contains('glass-off') === on) {
          glassMode = on; root.classList.toggle('glass-off', !on);
          if (!on) { if (ghost) maskStyle(ghost, 'none', 'auto', '0 0'); maskStyle(chaptersEl, 'none', 'auto', '0 0'); }
        }
        /* portrait / glass off: never fetch, decode or re-encode the matte (phones would pay ~1 MB and ~90 MB decoded for nothing) */
        if (!ghost || !G || !glassMode) return;
        var M = MATTE_GRID[setName], im = sheet(setName);
        if (!im.complete || !im.naturalWidth) return;
        var cw = im.naturalWidth / M.cols, chh = im.naturalHeight / M.rows;
        var shown = nearestIndex(current); if (shown < 0) shown = current;
        var key = setName + shown;
        if (key !== lastMaskKey) {
          if (mcv.width !== cw || mcv.height !== chh) { mcv.width = cw; mcv.height = chh; }
          mctx.clearRect(0, 0, cw, chh);
          mctx.drawImage(im, (shown % M.cols) * cw, Math.floor(shown / M.cols) * chh, cw, chh, 0, 0, cw, chh);
          matteDirty = true; lastMaskKey = key;
          if (kcv.width !== cw || kcv.height !== chh) { kcv.width = cw; kcv.height = chh; }
          kctx.clearRect(0, 0, cw, chh);
          for (var k = 0; k < 4; k++) kctx.drawImage(mcv, 0, 0);
          try { ghost._url = kcv.toDataURL('image/png'); } catch (e) { disableGlass(); return; }
        }
        var sz = rect.w + 'px ' + rect.h + 'px', pos = rect.x + 'px ' + rect.y + 'px';
        maskStyle(ghost, 'url(' + ghost._url + ')', sz, pos);
        maskStyle(chaptersEl, 'url(' + ghost._url + '), linear-gradient(#000, #000)', sz + ', 100% 100%', pos + ', 0 0');
        updateRefraction();
      }

      /* ---- chapter rail ---- */
      var rail = root.querySelector('[data-rail]');
      rail.innerHTML = '';
      var railFor = [];
      chapters.forEach(function (ch) {
        if (!ch.dataset.label) { railFor.push(null); return; }
        var li = document.createElement('li');
        var b = document.createElement('button'); b.type = 'button';
        var label = document.createElement('span'); label.textContent = ch.dataset.label || '';
        b.appendChild(label); b.appendChild(document.createElement('i'));
        b.addEventListener('click', function () { goTo(+ch.dataset.from + 2); });
        li.appendChild(b); rail.appendChild(li); railFor.push(b);
      });
      rail.hidden = false;
      this.cleanups.push(function () { rail.innerHTML = ''; rail.hidden = true; });

      var st = ScrollTrigger.create({
        trigger: root, start: 'top top', end: 'bottom bottom', scrub: true,
        onUpdate: function (s) { setFrame(Math.round(s.progress * (FRAMES - 1))); }
      });
      var shiftTween = null;
      this.cleanups.push(function () { st.kill(); if (shiftTween) shiftTween.kill(); });
      function goTo(frame) {
        var y = st.start + (st.end - st.start) * (frame / (FRAMES - 1));
        if (lenis) lenis.scrollTo(y, { duration: 1.2 }); else window.scrollTo({ top: y, behavior: 'smooth' });
      }
      var lastChapter = -1;
      function setFrame(f) {
        if (f !== current) { current = f; updateMask(); draw(f); }
        var active = 0;
        chapters.forEach(function (ch, idx) { if (f >= +ch.dataset.from) active = idx; });
        if (active !== lastChapter && chapters[active]) {
          /* every chapter stays in the accessibility tree; only the active one is shown (opacity), see focusin below */
          chapters.forEach(function (ch, idx) { ch.classList.toggle('on', idx === active); });
          ghostChapters.forEach(function (ch, idx) { ch.classList.toggle('on', idx === active); });
          var fx = chapters[active].classList.contains('right') ? -0.14 : 0.14;
          /* first paint lands in place; only chapter changes glide */
          if (lastChapter < 0) { shift.fx = fx; size(); }
          else shiftTween = gsap.to(shift, { fx: fx, duration: 0.9, ease: 'power2.inOut', overwrite: true, onUpdate: size });
          railFor.forEach(function (b, idx) { if (!b) return; if (idx === active) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current'); });
          lastChapter = active;
        }
        callouts.forEach(function (c) { c.el.classList.toggle('on', f >= c.a && f <= c.b); });
        if (intro) {
          var atIntro = chapters[active] === intro;
          root.style.setProperty('--intro-p', Math.min(1, f / (introTo + 1)).toFixed(3)); root.classList.toggle('at-intro', atIntro);
          /* the rail fades out over the intro: keep it out of the tab order while it can't be seen */
          rail.inert = atIntro; rail.toggleAttribute('inert', atIntro);
        }
      }
      this.cleanups.push(function () { rail.inert = false; rail.removeAttribute('inert'); });
      /* keyboard users tabbing into a chapter that isn't on screen (e.g. a CTA further down the story):
         jump the scroll to that chapter so the focused control is visible */
      this.on(chaptersEl, 'focusin', function (e) {
        var ch = e.target.closest && e.target.closest('.chapter'); if (!ch) return;
        var idx = chapters.indexOf(ch); if (idx < 0 || idx === lastChapter) return;
        var frame = Math.min(FRAMES - 1, +ch.dataset.from + 2);
        var y = st.start + (st.end - st.start) * (frame / (FRAMES - 1));
        if (lenis) lenis.scrollTo(y, { immediate: true, force: true }); else window.scrollTo(0, y);
        ScrollTrigger.update();
      });
      this.on(window, 'resize', size);
      size(); setFrame(0);
      /* the frame list and the page height settle after fonts and other sections load */
      this.on(window, 'load', function () { ScrollTrigger.refresh(); });

      /* in-page anchors glide with the smooth scroll and clear the sticky header */
      if (lenis) {
        this.on(document, 'click', function (e) {
          if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          /* the skip link keeps native behaviour: jump straight to <main> and move focus there */
          var a = e.target.closest && e.target.closest('a[href^="#"]:not(.skip-link)'); if (!a) return;
          var id = a.getAttribute('href'); if (id.length < 2) return;
          var t; try { t = document.querySelector(id); } catch (err) { return; }
          if (!t) return;
          var header = document.querySelector('.site-header');
          e.preventDefault();
          lenis.scrollTo(t, { offset: -(header ? header.offsetHeight : 64) });
          /* preventDefault also cancels the native focus move: hand focus to the target ourselves */
          if (!t.matches('a[href], button, input, select, textarea, [tabindex]')) t.setAttribute('tabindex', '-1');
          t.focus({ preventScroll: true });
        });
      }
    }
  }
  customElements.define('scroll-story', ScrollStory);
})();
